import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { CrudTable, type CrudColumn } from '@/shared/components/ui/crud-table';
import { useIsAdminTier } from '@/shared/auth/SessionProvider';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Textarea } from '@/shared/components/ui/textarea';
import {
  listAllParametros,
  updateParametro,
  type ParametroItem,
  SIRCREB_PARAM_NAME,
} from '@/shared/api/controllers/parametros';
import { invalidateAbmRelatedQueries } from '@/shared/api/abmCacheInvalidation';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';
import type { EntityViewProps } from '../../types';

/**
 * CRUD for `app_parameters` (system-wide knobs: Limite Nova,
 * Impuesto SIRCREB, retencion floor, …). Country-scoped implicitly
 * via the active country header — admins switching country will see
 * that country's rows.
 *
 * Edit-only: rows are seeded by migrations and never created from the
 * UI. SIRCREB has its own dedicated editor on the Home screen that also
 * back-fills the current month's MovimientoCajas placeholders, so we
 * disable the inline edit for that one row to avoid drift.
 *
 * Display vs storage: stored values are comma-decimal text (`'0,05'`,
 * `'1,05'`, `'15000'`) but admins shouldn't have to think about that.
 * `PARAM_FORMAT` classifies each known row so the table and the edit
 * dialog show `5 %` (Porcentaje), `5 %` (Multiplicador, the markup
 * portion above 1), or `15.000` (Monto). The wire format remains
 * unchanged — backend keeps reading `parseCommaDecimal('0,05')`.
 */

const QUERY_KEY = ['configuracion', 'parametros-sistema'] as const;

type ParamFormat = 'Porcentaje' | 'Multiplicador' | 'Monto' | 'Texto';

const PARAM_FORMAT: Record<string, ParamFormat> = {
  'Comision Nova': 'Porcentaje',
  'Impuesto SIRCREB': 'Porcentaje',
  'Impuesto Caja': 'Porcentaje',
  'Impuesto MercadoPago': 'Porcentaje',
  'Impuesto Morvalab': 'Porcentaje',
  'Impuesto Ley 25.413': 'Porcentaje',
  'Multiplicador Costo Contable': 'Multiplicador',
  'Limite Nova': 'Monto',
  'Limite Retenciones': 'Monto',
};

function formatFor(name: string | null | undefined): ParamFormat {
  return PARAM_FORMAT[name ?? ''] ?? 'Texto';
}

/** Parse a stored value (comma-decimal text) into a JS number. */
function parseStored(raw: string | number | null | undefined): number {
  if (raw == null) return NaN;
  if (typeof raw === 'number') return raw;
  const t = raw.trim();
  if (!t) return NaN;
  const n = Number(t.replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : NaN;
}

/** Format a JS number back to comma-decimal storage text. Trims trailing
 * zeros so `0.05 → '0,05'` and `15000 → '15000'` round-trip cleanly. */
function toStoredString(n: number): string {
  if (!Number.isFinite(n)) return '';
  const fixed = n.toFixed(6).replace(/0+$/, '').replace(/\.$/, '');
  return fixed.replace('.', ',');
}

/** Render a stored value as the table cell text. */
function formatDisplay(
  stored: string | number | null | undefined,
  format: ParamFormat,
): string {
  if (format === 'Texto') {
    return stored == null || stored === '' ? '—' : String(stored);
  }
  const n = parseStored(stored);
  if (!Number.isFinite(n)) return '—';
  if (format === 'Porcentaje') {
    return `${(n * 100).toLocaleString('es-AR', { maximumFractionDigits: 4 })} %`;
  }
  if (format === 'Multiplicador') {
    // 1.05 → 5 %  — show only the markup above 1 so the value lines up
    // with the other percentages in the table.
    return `${((n - 1) * 100).toLocaleString('es-AR', { maximumFractionDigits: 4 })} %`;
  }
  return n.toLocaleString('es-AR', { maximumFractionDigits: 4 });
}

/** Initial value to render in the input field — same display semantics as
 * the table cell, but without thousand separators (so the input parses). */
function storedToInputDisplay(
  stored: string | number | null | undefined,
  format: ParamFormat,
): string {
  if (format === 'Texto') return stored == null ? '' : String(stored);
  const n = parseStored(stored);
  if (!Number.isFinite(n)) return '';
  let displayN: number;
  if (format === 'Porcentaje') displayN = n * 100;
  else if (format === 'Multiplicador') displayN = (n - 1) * 100;
  else displayN = n;
  // Trim FP noise (2.5000000000000004 → "2,5"), keep up to 6 decimals.
  return toStoredString(displayN);
}

/** Inverse of `storedToInputDisplay` — what the admin typed → wire format. */
function inputDisplayToStored(input: string, format: ParamFormat): string {
  if (format === 'Texto') return input.trim();
  const n = parseStored(input);
  if (!Number.isFinite(n)) return '';
  let storedN: number;
  if (format === 'Porcentaje') storedN = n / 100;
  else if (format === 'Multiplicador') storedN = 1 + n / 100;
  else storedN = n;
  return toStoredString(storedN);
}

function suffixFor(format: ParamFormat): string {
  if (format === 'Porcentaje' || format === 'Multiplicador') return '%';
  return '';
}

export function ParametrosSistemaEntity({
  search,
  filterValue,
  onStatus,
}: EntityViewProps) {
  const queryClient = useQueryClient();
  const { config: activeCountry } = useActiveCountry();
  const scopedKey = useMemo(
    () => [...QUERY_KEY, activeCountry.code] as readonly unknown[],
    [activeCountry.code],
  );
  const {
    data: rows = [],
    isPending,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: scopedKey,
    queryFn: listAllParametros,
    staleTime: 60_000,
  });
  const [editing, setEditing] = useState<ParametroItem | null>(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = [...rows].sort((a, b) =>
      (a.fields.NombreParametro_P ?? '').localeCompare(
        b.fields.NombreParametro_P ?? '',
        'es',
      ),
    );
    if (filterValue) {
      list = list.filter((r) => r.fields.Status_P === filterValue);
    }
    if (!q) return list;
    return list.filter((r) =>
      (r.fields.NombreParametro_P ?? '').toLowerCase().includes(q),
    );
  }, [rows, search, filterValue]);

  const columns: CrudColumn<ParametroItem>[] = [
    {
      header: 'Parámetro',
      render: (r) => (
        <span className="font-medium text-foreground">
          {r.fields.NombreParametro_P || '—'}
        </span>
      ),
    },
    {
      header: 'Valor',
      render: (r) => formatDisplay(r.fields.Valor_P, formatFor(r.fields.NombreParametro_P)),
    },
    {
      header: 'Descripción',
      render: (r) => (
        <span className="text-sm text-muted-foreground">
          {r.fields.Descripcion_P || '—'}
        </span>
      ),
    },
    {
      // Un valor de acá cambia comportamiento fiscal en varios módulos a la
      // vez — poner la tasa de Ley 25.413 en 0 apagó el impuesto automático
      // en Compras y Tesorería, y reconstruir quién lo hizo llevó un rato
      // largo de forense. Que se vea en la grilla.
      header: 'Última modificación',
      render: (r) => (
        <div className="text-sm leading-tight">
          <div className="text-foreground">{r.fields.FechaActualizacion_P || '—'}</div>
          <div className="text-xs text-muted-foreground">
            {r.fields.UsuarioActualizacion_P || 'sin registro'}
          </div>
        </div>
      ),
    },
    {
      header: 'Status',
      width: 'w-32',
      render: (r) => (
        <span className="text-muted-foreground">{r.fields.Status_P || '—'}</span>
      ),
    },
  ];

  const canEdit = useIsAdminTier();
  return (
    <>
      <CrudTable
        hideActions={!canEdit}
        rows={filtered}
        columns={columns}
        loading={isPending}
        error={error instanceof Error ? error : null}
        onRetry={() => refetch()}
        emptyMessage={search ? `No hay resultados para "${search}".` : 'Sin parámetros.'}
        onEdit={(r) => setEditing(r)}
        canEdit={(r) => r.fields.NombreParametro_P !== SIRCREB_PARAM_NAME}
        editDisabledTitle={(r) =>
          r.fields.NombreParametro_P === SIRCREB_PARAM_NAME
            ? 'Editar SIRCREB desde la pantalla de Inicio (también recalcula los movimientos del mes).'
            : undefined
        }
        isBackgroundFetching={isFetching}
      />

      {editing && (
        <ParametroSistemaEditDialog
          item={editing}
          open
          onOpenChange={(o) => !o && setEditing(null)}
          onSuccess={(msg) => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEY });
            invalidateAbmRelatedQueries(queryClient, 'parametros-sistema');
            setEditing(null);
            onStatus({ kind: 'success', message: msg });
          }}
          onError={(msg) => {
            setEditing(null);
            onStatus({ kind: 'error', message: msg });
          }}
        />
      )}
    </>
  );
}

const schema = z.object({
  valor: z.string().trim().min(1, 'El valor es obligatorio'),
  descripcion: z.string().trim().nullable(),
});
type Values = z.infer<typeof schema>;

function ParametroSistemaEditDialog({
  item,
  open,
  onOpenChange,
  onSuccess,
  onError,
}: {
  item: ParametroItem;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}) {
  const format = formatFor(item.fields.NombreParametro_P);
  const suffix = suffixFor(format);

  const defaults: Values = {
    valor: storedToInputDisplay(item.fields.Valor_P, format),
    descripcion: item.fields.Descripcion_P ?? '',
  };

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: defaults,
  });

  useEffect(() => {
    if (open) form.reset(defaults);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item.id]);

  const mutation = useMutation({
    mutationFn: async (values: Values) => {
      const stored = inputDisplayToStored(values.valor, format);
      if (!stored) {
        throw new Error('Valor numérico inválido');
      }
      return updateParametro(item.id, {
        valor: stored,
        descripcion: values.descripcion?.trim() || null,
      });
    },
    onSuccess: () => onSuccess('Parámetro actualizado correctamente.'),
    onError: (err) =>
      onError(err instanceof Error ? err.message : 'No se pudo actualizar el parámetro.'),
  });

  const onSubmit = form.handleSubmit((values) => mutation.mutate(values));

  const helperText =
    format === 'Porcentaje'
      ? 'Ingrese el porcentaje (ej. 5 para 5%).'
      : format === 'Multiplicador'
        ? 'Ingrese el porcentaje del recargo (ej. 5 para multiplicar por 1,05).'
        : format === 'Monto'
          ? 'Ingrese el monto sin separadores de miles.'
          : 'Use coma para decimales (ej. 0,05).';

  return (
    <Dialog open={open} onOpenChange={(o) => !mutation.isPending && onOpenChange(o)}>
      <DialogContent
        className="sm:max-w-md"
        processing={mutation.isPending && 'Guardando cambios…'}
      >
        <DialogHeader>
          <DialogTitle>Editar parámetro</DialogTitle>
          <DialogDescription>
            {item.fields.NombreParametro_P ?? 'Parámetro'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="flex flex-1 flex-col overflow-hidden" noValidate>
          <DialogBody className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="valor">Valor</Label>
              <div className="relative">
                <Input
                  id="valor"
                  variant="filled"
                  className={suffix ? 'pr-10' : undefined}
                  {...form.register('valor')}
                  aria-invalid={!!form.formState.errors.valor}
                />
                {suffix && (
                  <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                    {suffix}
                  </span>
                )}
              </div>
              {form.formState.errors.valor?.message && (
                <p className="text-xs font-medium text-destructive">
                  {form.formState.errors.valor.message}
                </p>
              )}
              <p className="text-xs text-muted-foreground">{helperText}</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="descripcion">Descripción</Label>
              <Textarea
                id="descripcion"
                rows={3}
                {...form.register('descripcion')}
              />
            </div>
          </DialogBody>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              Guardar cambios
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
